import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  IconButton,
  Grow,
  Fade,
  useTheme,
  useMediaQuery,
  Chip,
  alpha,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Close,
  Star,
  AutoAwesome,
  TrendingUp,
  Diamond,
} from '@mui/icons-material'
import {
  subscriptionPlans,
  SubscriptionPlan,
  MembershipPlan,
  MembershipPlansResponse,
} from '../types/subscription'
import { membershipApi } from '../services/api'
import { bi } from 'node_modules/@fullcalendar/core/internal-common'

interface ServicePackagePlansProps {
  open: boolean
  onClose: () => void
  onPlanSelect: (
    plan: MembershipPlan,
    billingCycle: 'monthly' | 'yearly',
    amountPaid: string,
    totalPrice: string,
    couponApplied: string
  ) => void
}

export const SubscriptionPlans: React.FC<ServicePackagePlansProps> = ({
  open,
  onClose,
  onPlanSelect,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(
    'monthly'
  )
  const [plans, setPlans] = useState<MembershipPlan[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [couponInputs, setCouponInputs] = useState<{ [key: string]: string }>(
    {}
  )
  const [discountedAmounts, setDiscountedAmounts] = useState<{
    [key: string]: number
  }>({})
  const [appliedCoupons, setAppliedCoupons] = useState<{
    [key: string]: string
  }>({}) // Track applied coupons per plan
  const [couponLoading, setCouponLoading] = useState<{
    [key: string]: boolean
  }>({})
  const [toast, setToast] = useState<{
    open: boolean
    message: string
    type: 'success' | 'error'
  }>({
    open: false,
    message: '',
    type: 'success',
  })

  // Fetch plans when dialog opens
  useEffect(() => {
    if (open) {
      fetchPlans()
    }
  }, [open])

  const fetchPlans = async () => {
    try {
      setLoading(true)
      setError(null)
      const response: MembershipPlansResponse = await membershipApi.getPlans(
        1,
        10
      )
      setPlans(response.membership || [])
    } catch (err) {
      console.error('Error fetching plans:', err)
      setError('Failed to load subscription plans. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handlePlanSelect = (plan: MembershipPlan) => {
    const monthlyPrice =
      billingCycle === 'monthly'
        ? plan.monthly_fee
        : Math.round(plan.anual_fee)



    const finalAmount =
      discountedAmounts[plan._id] !== undefined
        ? discountedAmounts[plan._id]
        : monthlyPrice

    // Get the applied coupon for this specific plan
    const couponApplied = appliedCoupons[plan._id] || ''

    onPlanSelect(
      plan,
      billingCycle,
      finalAmount.toString(),
      finalAmount.toString(),
      couponApplied
    )
    // }
  }

  const applyCoupon = async (planId: string, amount: number) => {
    const couponCode = couponInputs[planId]
    if (!couponCode) {
      setToast({
        open: true,
        message: 'Please enter a coupon code',
        type: 'error',
      })
      return
    }

    try {
      setCouponLoading(prev => ({ ...prev, [planId]: true }))

      const res = await membershipApi.getCoupon(couponCode, amount)

      setDiscountedAmounts(prev => ({
        ...prev,
        [planId]: res.finalAmount,
      }))

      // Store the applied coupon for this plan
      setAppliedCoupons(prev => ({
        ...prev,
        [planId]: couponCode,
      }))

      setToast({
        open: true,
        message: 'Coupon applied successfully!',
        type: 'success',
      })
    } catch (err: any) {
      console.error(err)
      setToast({
        open: true,
        message: err?.response?.data?.message || 'Invalid coupon code',
        type: 'error',
      })
    } finally {
      setCouponLoading(prev => ({ ...prev, [planId]: false }))
    }
  }

  const removeCoupon = (planId: string) => {
    const monthlyPrice =
      billingCycle === 'monthly'
        ? plans.find(p => p._id === planId)?.monthly_fee || 0
        : Math.round((plans.find(p => p._id === planId)?.anual_fee || 0) / 12)

    // Remove discount and coupon
    setDiscountedAmounts(prev => {
      const newDiscounted = { ...prev }
      delete newDiscounted[planId]
      return newDiscounted
    })

    setAppliedCoupons(prev => {
      const newApplied = { ...prev }
      delete newApplied[planId]
      return newApplied
    })

    // Clear coupon input
    setCouponInputs(prev => ({
      ...prev,
      [planId]: '',
    }))

    // setToast({
    //   open: true,
    //   message: 'Coupon removed successfully!',
    //   type: 'success',
    // })
  }

  useEffect(() => {
    if (toast.open) {
      const timer = setTimeout(() => {
        setToast(prev => ({ ...prev, open: false }))
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [toast.open])

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 4,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          },
        }}
      >
        <Fade in={open} timeout={600}>
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pb: 2,
              pt: { xs: 4, md: 6 },
              px: { xs: 2, md: 4 },
              background: 'transparent',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                maxWidth: '700px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                {/* Decorative element */}
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: '16px',
                    background:
                      'linear-gradient(135deg, #2E7DAF 0%, #1e5a8a 100%)',
                    display: 'inline-flex',
                    boxShadow: '0 4px 14px 0 rgba(46, 125, 175, 0.39)',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': {
                        boxShadow: '0 4px 14px 0 rgba(46, 125, 175, 0.39)',
                      },
                      '50%': {
                        boxShadow: '0 4px 20px 0 rgba(46, 125, 175, 0.6)',
                      },
                      '100%': {
                        boxShadow: '0 4px 14px 0 rgba(46, 125, 175, 0.39)',
                      },
                    },
                  }}
                >
                  <AutoAwesome sx={{ color: 'white', fontSize: '2rem' }} />
                </Box>

                <Typography
                  variant="h3"
                  fontWeight={700}
                  gutterBottom
                  sx={{
                    background:
                      'linear-gradient(135deg, #1e293b 0%, #2E7DAF 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1.5,
                    fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                    letterSpacing: '-0.02em',
                  }}
                >
                  Choose Your Perfect Plan
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: '#64748b',
                  fontWeight: 400,
                  fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  lineHeight: 1.6,
                  maxWidth: '550px',
                }}
              >
                Choose a plan to get started on our platform and reach new
                patients.
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              size="large"
              sx={{
                position: 'absolute',
                right: { xs: 8, md: 16 },
                top: { xs: 8, md: 16 },
                bgcolor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&:hover': {
                  bgcolor: '#f1f5f9',
                  transform: 'rotate(90deg) scale(1.1)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
        </Fade>

        <DialogContent
          sx={{
            pt: { xs: '1rem !important', md: '1rem !important' },
            px: { xs: 2, md: 6 },
            pb: { xs: 4, md: 8 },
            bgcolor: 'transparent',
          }}
        >
          {/* Plan Cards */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </Box>
          )}

          {!loading && !error && (
            <Grid container spacing={3} justifyContent="center">
              {plans.map((plan, index) => (
                <Grid item xs={12} md={6} key={plan._id}>
                  <Grow
                    in={open}
                    timeout={600 + index * 200}
                    style={{ transformOrigin: 'center top' }}
                  >
                    <Box sx={{ position: 'relative', height: '100%' }}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 4,
                          border: '1px solid #e2e8f0',
                          background: 'white',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          overflow: 'visible',
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                          '&:hover': {
                            transform: 'translateY(-8px) scale(1.02)',
                            boxShadow: '0 20px 40px rgba(46, 125, 175, 0.15)',
                            borderColor: '#2E7DAF',
                            '& .plan-icon': {
                              transform: 'scale(1.1) rotate(5deg)',
                            },
                            '& .plan-price': {
                              transform: 'scale(1.05)',
                            },
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background:
                              'linear-gradient(90deg, #2E7DAF 0%, #1e5a8a 100%)',
                            borderRadius: '16px 16px 0 0',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            p: { xs: 3, md: 4 },
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* Plan Header */}
                          <Box sx={{ mb: 3 }}>
                            {/* Plan Icon & Name */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 3,
                              }}
                            >
                              <Box
                                className="plan-icon"
                                sx={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: 3,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background:
                                    'linear-gradient(135deg, #2E7DAF 0%, #1e5a8a 100%)',
                                  mr: 2,
                                  boxShadow:
                                    '0 4px 14px rgba(46, 125, 175, 0.3)',
                                  transition: 'all 0.3s ease',
                                }}
                              >
                                {plan.name.toLowerCase().includes('starter') ? (
                                  <Star
                                    sx={{
                                      color: 'white',
                                      fontSize: '1.75rem',
                                    }}
                                  />
                                ) : (
                                  <Diamond
                                    sx={{
                                      color: 'white',
                                      fontSize: '1.75rem',
                                    }}
                                  />
                                )}
                              </Box>
                              <Box>
                                <Typography
                                  variant="h5"
                                  fontWeight={700}
                                  sx={{
                                    color: '#1e293b',
                                    fontSize: '1.5rem',
                                    mb: 0.5,
                                  }}
                                >
                                  {plan.name}
                                </Typography>
                              </Box>
                            </Box>

                            {/* Pricing */}
                            <Box>
                              <Box
                                className="plan-price"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'baseline',
                                  mb: 1,
                                  transition: 'all 0.3s ease',
                                }}
                              >
                                <Typography
                                  variant="h2"
                                  fontWeight={800}
                                  sx={{
                                    color: '#0f172a',
                                    lineHeight: 1,
                                    fontSize: '3.5rem',
                                    background:
                                      'linear-gradient(135deg, #2E7DAF 0%, #1e5a8a 100%)',
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                  }}
                                >
                                  $
                                  {discountedAmounts[plan._id] !== undefined
                                    ? discountedAmounts[plan._id]
                                    : billingCycle === 'monthly'
                                      ? plan.monthly_fee
                                      : Math.round(plan.anual_fee / 12)}
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: '#64748b',
                                    ml: 1,
                                    fontWeight: 500,
                                  }}
                                >
                                  /Month
                                </Typography>
                              </Box>

                              {/* Show original price if discounted */}
                              {discountedAmounts[plan._id] !== undefined && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#94a3b8',
                                    textDecoration: 'line-through',
                                    fontSize: '1rem',
                                  }}
                                >
                                  $
                                  {billingCycle === 'monthly'
                                    ? plan.monthly_fee
                                    : Math.round(plan.anual_fee / 12)}
                                  {billingCycle === 'monthly' ? ' /Monthly' : ' /Yearly'}
                                </Typography>
                              )}

                              {/* Annual pricing details */}
                              {/* {billingCycle === 'yearly' && ( */}
                              <Box
                                sx={{
                                  bgcolor: alpha('#2E7DAF', 0.05),
                                  border: `1px solid ${alpha('#2E7DAF', 0.15)}`,
                                  borderRadius: 3,
                                  p: 2,
                                  mb: 2,
                                  position: 'relative',
                                  overflow: 'hidden',
                                  animation: 'slideIn 0.5s ease',
                                  '@keyframes slideIn': {
                                    from: {
                                      opacity: 0,
                                      transform: 'translateY(-10px)',
                                    },
                                    to: {
                                      opacity: 1,
                                      transform: 'translateY(0)',
                                    },
                                  },
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '3px',
                                    background:
                                      'linear-gradient(90deg, #2E7DAF 0%, #1e5a8a 100%)',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    mb: 1,
                                  }}
                                >

                                  {billingCycle === 'yearly' && (

                                    <><Box
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        bgcolor: '#2E7DAF',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 2px 8px rgba(46, 125, 175, 0.3)',
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          color: 'white',
                                          fontSize: '0.7rem',
                                          fontWeight: 700,
                                        }}
                                      >
                                        $
                                      </Typography>
                                    </Box><Typography
                                      variant="body2"
                                      sx={{
                                        color: '#2E7DAF',
                                        fontWeight: 700,
                                        fontSize: '0.9rem',
                                      }}
                                    >
                                        ${plan.anual_fee} billed annually
                                      </Typography></>
                                  )}
                                </Box>


                                {billingCycle === 'monthly' ? (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#64748b',
                                      fontSize: '0.8rem',
                                      fontWeight: 500,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    {/* <Box
                                      sx={{
                                        width: 4,
                                        height: 4,
                                        borderRadius: '50%',
                                        bgcolor: '#10B981',
                                      }}
                                    /> */}

                                    {plan.month_discount}
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#64748b',
                                      fontSize: '0.8rem',
                                      fontWeight: 500,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    {/* <Box
                                      sx={{
                                        width: 4,
                                        height: 4,
                                        borderRadius: '50%',
                                        bgcolor: '#10B981',
                                      }}
                                    /> */}

                                    {plan.year_discount}
                                    year
                                  </Typography>
                                )
                                }
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#64748b',
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  {/* <Box
                                    sx={{
                                      width: 4,
                                      height: 4,
                                      borderRadius: '50%',
                                      bgcolor: '#10B981',
                                    }}
                                  /> */}

                                  {plan.commission}% Per Claim System Fee
                                </Typography>

                              </Box>

                              {/* )} */}

                              {billingCycle === 'monthly' &&
                                discountedAmounts[plan._id] === undefined && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#64748b',
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    Billed monthly
                                  </Typography>
                                )}
                            </Box>
                          </Box>

                          {/* Billing Toggle */}
                          <Box sx={{ mb: 3 }}>
                            <Box
                              sx={{
                                bgcolor: '#F3F4F6',
                                borderRadius: 2,
                                p: 0.5,
                                display: 'flex',
                              }}
                            >
                              <Button
                                onClick={() => { setBillingCycle('monthly'); removeCoupon(plan._id) }}
                                sx={{
                                  flex: 1,
                                  py: 1.5,
                                  px: 2,
                                  borderRadius: 1.5,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  bgcolor:
                                    billingCycle === 'monthly'
                                      ? '#2E7DAF'
                                      : 'transparent',
                                  color:
                                    billingCycle === 'monthly'
                                      ? 'white'
                                      : '#6B7280',
                                  boxShadow:
                                    billingCycle === 'monthly'
                                      ? '0 1px 3px rgba(0,0,0,0.1)'
                                      : 'none',
                                  '&:hover': {
                                    bgcolor:
                                      billingCycle === 'monthly'
                                        ? '#1e5a8a'
                                        : 'rgba(0,0,0,0.02)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                Pay Monthly
                              </Button>
                              <Button
                                onClick={() => { setBillingCycle('yearly'); removeCoupon(plan._id) }}
                                sx={{
                                  flex: 1,
                                  py: 1.5,
                                  px: 2,
                                  borderRadius: 1.5,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  bgcolor:
                                    billingCycle === 'yearly'
                                      ? '#2E7DAF'
                                      : 'transparent',
                                  color:
                                    billingCycle === 'yearly'
                                      ? 'white'
                                      : '#6B7280',
                                  boxShadow:
                                    billingCycle === 'yearly'
                                      ? '0 1px 3px rgba(0,0,0,0.1)'
                                      : 'none',
                                  '&:hover': {
                                    bgcolor:
                                      billingCycle === 'yearly'
                                        ? '#1e5a8a'
                                        : 'rgba(0,0,0,0.02)',
                                  },
                                  transition: 'all 0.2s ease',
                                }}
                              >
                                Pay Annually
                              </Button>
                            </Box>
                          </Box>

                          {/* Commission Info & Trial Days
                          {((billingCycle === 'monthly' &&
                            plan.monthly_commission) ||
                            (billingCycle === 'yearly' &&
                              plan.anual_commission) ||
                            (plan.trialDays && plan.trialDays > 0)) && (
                              <Box
                                sx={{
                                  bgcolor: alpha('#2E7DAF', 0.1),
                                  border: `1px solid ${alpha('#2E7DAF', 0.2)}`,
                                  borderRadius: 2,
                                  p: 1.5,
                                  mb: 3,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                }}
                              >
                                {billingCycle === 'monthly' &&
                                  plan.commission && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#2E7DAF',
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {plan.commission}% Per Claim System Fee
                                    </Typography>
                                  )}
                                {billingCycle === 'yearly' &&
                                  plan.commission && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#2E7DAF',
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {plan.commission}% Per-Claim System Fee
                                    </Typography>
                                  )}
                                {plan.trialDays > 0 && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#2E7DAF',
                                      fontSize: '0.85rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {plan.trialDays}-Day Free Trial Included
                                  </Typography>
                                )}
                              </Box>
                            )} */}

                          {/* Coupon Section */}
                          <Box sx={{ mb: 2, mt: 1 }}>
                            {discountedAmounts[plan._id] !== undefined ? (
                              // Show applied coupon with remove option
                              <Box>
                                <Typography
                                  sx={{
                                    mt: 1,
                                    color: '#16a34a',
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    mb: 1,
                                  }}
                                >
                                  🎉 Coupon applied! New price: $
                                  {discountedAmounts[plan._id]}/Month
                                </Typography>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => removeCoupon(plan._id)}
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  Remove Coupon
                                </Button>
                              </Box>
                            ) : (
                              // Show coupon input field
                              <>
                                <Typography
                                  sx={{
                                    fontSize: '0.85rem',
                                    color: '#64748b',
                                    mb: 0.5,
                                  }}
                                >
                                  Have a coupon?
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <input
                                    value={couponInputs[plan._id] || ''}
                                    onChange={e =>
                                      setCouponInputs(prev => ({
                                        ...prev,
                                        [plan._id]: e.target.value,
                                      }))
                                    }
                                    placeholder="Enter coupon"
                                    style={{
                                      flex: 1,
                                      padding: '10px 12px',
                                      borderRadius: '8px',
                                      border: '1px solid #cbd5e1',
                                      outline: 'none',
                                      fontSize: '0.9rem',
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    sx={{
                                      borderRadius: 2,
                                      textTransform: 'none',
                                      fontWeight: 600,
                                    }}
                                    onClick={() =>
                                      applyCoupon(
                                        plan._id,
                                        billingCycle === 'monthly'
                                          ? plan.monthly_fee
                                          : Math.round(plan.anual_fee / 12)
                                      )
                                    }
                                    disabled={couponLoading[plan._id]}
                                  >
                                    {couponLoading[plan._id] ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      'Apply'
                                    )}
                                  </Button>
                                </Box>
                              </>
                            )}
                          </Box>

                          {/* CTA Button */}
                          <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={!plan.isActive || plan.isDeleted}
                            onClick={() => handlePlanSelect(plan)}
                            sx={{
                              py: 2,
                              fontSize: '1.05rem',
                              fontWeight: 700,
                              borderRadius: 3,
                              textTransform: 'none',
                              background:
                                'linear-gradient(135deg, #2E7DAF 0%, #1e5a8a 100%)',
                              color: 'white',
                              boxShadow: '0 4px 16px rgba(46, 125, 175, 0.4)',
                              '&:hover': {
                                background:
                                  'linear-gradient(135deg, #1e5a8a 0%, #164e63 100%)',
                                boxShadow: '0 8px 24px rgba(46, 125, 175, 0.5)',
                                transform: 'translateY(-2px)',
                              },
                              '&:disabled': {
                                bgcolor: '#e2e8f0',
                                color: '#94a3b8',
                              },
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {!plan.isActive || plan.isDeleted
                              ? 'Unavailable'
                              : 'Select Plan'}
                          </Button>
                        </CardContent>
                      </Card>
                    </Box>
                  </Grow>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {toast.open && (
        <Alert
          severity={toast.type}
          onClose={() => setToast({ ...toast, open: false })}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 2000,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          }}
        >
          {toast.message}
        </Alert>
      )}
    </>
  )
}

export default SubscriptionPlans
